import React from 'react';
import {
  LayoutGrid,
  ListChecks,
  BarChart3,
  Workflow,
  FileBarChart,
  Search,
  Bell,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
} from 'lucide-react';

const navItems = [
  { icon: LayoutGrid, label: 'Overview', active: true },
  { icon: ListChecks, label: 'Leads' },
  { icon: Workflow, label: 'Workflows' },
  { icon: BarChart3, label: 'Analytics' },
  { icon: FileBarChart, label: 'Reports' },
];

const kpis = [
  { label: 'New leads (24h)', value: '1,284', delta: '+12.4%', up: true },
  { label: 'Qualified', value: '317', delta: '+5.8%', up: true },
  { label: 'Avg. score', value: '68', delta: '+3', up: true },
  { label: 'Response time', value: '11m', delta: '-32%', up: false },
];

const leads = [
  { co: 'Northwind Cloud', ind: 'Cloud Infra', score: 92, status: 'Qualified', src: 'Content', color: 'emerald' },
  { co: 'Meridian Analytics', ind: 'Fintech · SaaS', score: 74, status: 'Nurture', src: 'Webinar', color: 'blue' },
  { co: 'Halcyon Logistics', ind: 'Industrial', score: 61, status: 'Nurture', src: 'Syndication', color: 'blue' },
  { co: 'Vertex Studios', ind: 'Media', score: 48, status: 'Review', src: 'Paid social', color: 'amber' },
  { co: 'Stellar Robotics', ind: 'Hardware', score: 33, status: 'Cold', src: 'List', color: 'slate' },
];

const statusStyles = {
  emerald: 'bg-emerald-50 text-emerald-700',
  blue: 'bg-brand-blue/10 text-brand-blue',
  amber: 'bg-amber-50 text-amber-700',
  slate: 'bg-slate-100 text-slate-600',
};

const AALIGNMockup = () => {
  return (
    <div className="relative rounded-2xl overflow-hidden shadow-mk-lg bg-white ring-1 ring-slate-200">
      {/* Browser chrome */}
      <div className="flex items-center justify-between bg-navy-deep px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
          <span className="ml-4 inline-flex items-center gap-1.5 rounded-md bg-brand-pink/90 text-white text-[10px] font-bold uppercase tracking-[0.14em] px-2 py-0.5">
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            Live demo
          </span>
        </div>
        <div className="text-slate-400 text-[11px] font-bold tracking-[0.14em]">aalign.5dbs.io</div>
        <div className="w-16" />
      </div>

      {/* App body */}
      <div className="grid grid-cols-12 bg-white min-h-[520px]">
        {/* Sidebar */}
        <aside className="hidden md:flex md:col-span-3 lg:col-span-2 bg-brand-soft border-r border-slate-200 flex-col p-3">
          <div className="px-3 py-2 mb-3 flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-brand-pink to-brand-pink/70 text-white flex items-center justify-center shadow-[0_4px_11px_0_rgba(233,30,99,0.35)]">
              <Sparkles size={14} strokeWidth={2.6} />
            </div>
            <span className="text-navy font-black text-[13px] tracking-tight">AALIGN</span>
          </div>
          <nav className="flex flex-col gap-1">
            {navItems.map((n) => {
              const Icon = n.icon;
              return (
                <div
                  key={n.label}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-semibold ${
                    n.active ? 'bg-white text-navy shadow-mk-sm' : 'text-body-muted hover:bg-white/70'
                  }`}
                >
                  <Icon size={14} strokeWidth={2.2} />
                  {n.label}
                </div>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <section className="col-span-12 md:col-span-9 lg:col-span-10 p-5 sm:p-6">
          {/* Top bar */}
          <div className="flex items-center justify-between gap-3 mb-5">
            <div>
              <p className="text-body-muted text-[10.5px] font-bold uppercase tracking-[0.14em]">Workspace · Publisher A</p>
              <h4 className="mt-0.5 text-navy text-[19px] font-black tracking-tight">Lead pipeline</h4>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-body-muted text-[12px] w-56">
                <Search size={13} />
                <span>Search leads…</span>
              </div>
              <div className="h-9 w-9 rounded-lg border border-slate-200 flex items-center justify-center text-body-muted">
                <Bell size={15} />
              </div>
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-brand-blue to-brand-blue-light text-white flex items-center justify-center text-[11px] font-black">EM</div>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {kpis.map((k) => (
              <div key={k.label} className="rounded-xl bg-brand-soft p-3">
                <p className="text-body-muted text-[10px] font-bold uppercase tracking-[0.14em]">{k.label}</p>
                <div className="mt-1 flex items-end justify-between">
                  <span className="text-navy text-[22px] font-black leading-none">{k.value}</span>
                  <span
                    className={`inline-flex items-center gap-0.5 text-[10.5px] font-black ${
                      k.up ? 'text-emerald-600' : 'text-emerald-600'
                    }`}
                  >
                    {k.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    {k.delta}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Leads table */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="grid grid-cols-12 items-center px-4 py-2.5 bg-brand-soft text-body-muted text-[10px] font-bold uppercase tracking-[0.14em]">
              <div className="col-span-5">Company</div>
              <div className="col-span-2">Score</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-1 text-right">Act</div>
            </div>
            {leads.map((l) => (
              <div
                key={l.co}
                className="grid grid-cols-12 items-center px-4 py-3 border-t border-slate-100 text-[12.5px]"
              >
                <div className="col-span-5">
                  <p className="text-navy font-bold truncate">{l.co}</p>
                  <p className="text-body-muted text-[11px] truncate">{l.ind}</p>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-brand-pink to-brand-pink/70"
                        style={{ width: `${l.score}%` }}
                      />
                    </div>
                    <span className="text-navy font-black">{l.score}</span>
                  </div>
                </div>
                <div className="col-span-2">
                  <span
                    className={`inline-block text-[10.5px] font-bold uppercase tracking-[0.12em] rounded-full px-2 py-0.5 ${statusStyles[l.color]}`}
                  >
                    {l.status}
                  </span>
                </div>
                <div className="col-span-2 text-body-muted">{l.src}</div>
                <div className="col-span-1 text-right">
                  <button className="text-brand-pink font-bold text-[11px] uppercase tracking-[0.1em]">Open</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AALIGNMockup;
